import { InterfaceTheme } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';

/**
 * The theme the operating system asks for, as reported by `useSystemTheme`.
 */
export type SystemTheme = 'light' | 'dark';

/**
 * Is the given preference rendered as the dark theme right now?
 *
 * `system` (and an absent preference) follows the operating system, an explicit
 * `light` / `dark` does not.
 */
export const isDarkTheme = (
  theme: InterfaceTheme | undefined,
  systemTheme: SystemTheme,
): boolean => {
  if (theme === InterfaceTheme.dark) {
    return true;
  }

  if (theme === InterfaceTheme.light) {
    return false;
  }

  return systemTheme === 'dark';
};

/**
 * Which theme should be written to local storage once the user's profile has loaded?
 *
 * A theme stored in this browser always wins; the profile only seeds a browser that holds
 * none. The profile cannot win, because it cannot be read back in time: the theme is written
 * to the global identity service and only reaches the regional Control Room database through
 * an asynchronous cross-account event pipeline, while the profile is refetched as soon as that
 * write returns. The theme that comes back is therefore usually the *previous* one, so letting
 * it win overwrote the theme the user had just chosen, one save behind. SSO realm users have
 * no profile theme at all, so for them the browser is the only store there is.
 *
 * Returns `undefined` when the stored theme should be left alone.
 */
export const resolveSyncedTheme = ({
  userTheme,
  storedTheme,
}: {
  userTheme: InterfaceTheme | undefined;
  storedTheme: InterfaceTheme | undefined;
}): InterfaceTheme | undefined => {
  if (storedTheme) {
    return undefined;
  }

  /**
   * The default theme has been changed and is explicitly set to `system` for new users,
   * fallback to the default `dark` theme for users that have no explicit theme anywhere.
   */
  return userTheme ?? InterfaceTheme.dark;
};
